import React, { Component } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { openModal } from 'reducers/modals';
// regional date handling
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser } from 'utils/regionalDateHelper';
// components
import { FormattedMessage } from 'react-intl';
import Preloader from 'components/Preloader';
import InfoTooltip from 'components/InfoTooltip';
import DataTable from 'components/DataTable/DataTable';
import NothingBox from 'components/NothingBox';
import StatusBadge from 'components/StatusBadge/StatusBadge';
import { CustomColumn } from 'components/DataTable/Columns';
import ArrowColumn from 'components/DataTable/Columns/ArrowColumn';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import ManageSensorsModal from './ManageSensorsModal';
import { isPublicApiIntegration } from './barnToolsConstants';
// hooks
import { useDropdownActions } from 'hooks/useDropdownContext';
// utils
import { fetchFromAPI } from 'utils/api';
import { showToastrMessage } from 'utils';
import { isTablet } from 'react-device-detect';
// styles
import './BarnToolsList.scss';

const withDropdownActions = (WrappedComponent) => {
  return function WithDropdownActionsComponent(props) {
    const { openDropdown } = useDropdownActions();
    return <WrappedComponent {...props} openDropdown={openDropdown} />;
  };
};

class BarnToolsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      integrations: [],
      isLoading: true,
      sort: '',
      page: 1,
      per_page: 25,
      total: 0,
    };
  }

  componentDidMount() {
    this.fetchIntegrations();
  }

  fetchIntegrations = () => {
    const { companyId } = this.props;
    const { sort, page, per_page } = this.state;

    if (!companyId) {
      this.setState({ isLoading: false });
      return;
    }

    fetchFromAPI(`/admin/companies/${companyId}/barn_tools_integrations`, {
      params: { sort, page, per_page }
    })
      .then(({ resources, meta }) => {
        this.setState({
          isLoading: false,
          integrations: resources || [],
          total: meta?.total || 0,
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching integrations:', error);
        this.setState({ isLoading: false });
      });
  };

  handleSortChange = (sort) => {
    this.setState({ sort, page: 1 }, () => {
      this.fetchIntegrations();
    });
  };

  handlePageChange = (page) => {
    this.setState({ page }, () => {
      this.fetchIntegrations();
    });
  };

  handlePerPageChange = (per_page) => {
    this.setState({ per_page, page: 1 }, () => {
      this.fetchIntegrations();
    });
  };

  handleDeactivate = (integration) => {
    const { companyId } = this.props;

    fetchFromAPI(`/admin/companies/${companyId}/barn_tools_integrations/${integration.id}`, {
      method: 'PUT',
      body: JSON.stringify({ active: false }),
    })
      .then(() => {
        showToastrMessage('component.toastr.barnTools.success.deactivated');
        this.fetchIntegrations();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error deactivating integration:', error);
        showToastrMessage('component.toastr.barnTools.error.deactivationFailed', 'error');
      });
  };

  handleActivate = (integration) => {
    const { companyId } = this.props;

    fetchFromAPI(`/admin/companies/${companyId}/barn_tools_integrations/${integration.id}`, {
      method: 'PUT',
      body: JSON.stringify({ active: true }),
    })
      .then(() => {
        showToastrMessage('component.toastr.barnTools.success.activated');
        this.fetchIntegrations();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error activating integration:', error);
        showToastrMessage('component.toastr.barnTools.error.activationFailed', 'error');
      });
  };

  handleDelete = (integration) => {
    const { companyId } = this.props;

    fetchFromAPI(`/admin/companies/${companyId}/barn_tools_integrations/${integration.id}`, {
      method: 'DELETE',
    })
      .then(() => {
        showToastrMessage('component.toastr.barnTools.success.deleted');
        this.fetchIntegrations();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error deleting integration:', error);
        showToastrMessage('component.toastr.barnTools.error.deletionFailed', 'error');
      });
  };

  openDeleteConfirmationModal = (integration) => {
    const { openModal } = this.props;
    openModal(
      <ConfirmationModal
        title={<FormattedMessage id="component.modal.deleteBarnToolsIntegration.title" />}
        actionBtnLabel={<FormattedMessage id="component.modal.deleteBarnToolsIntegration.confirm" />}
        actionBtnProps={{ red: true }}
        handleConfirm={() => this.handleDelete(integration)}
      >
        <FormattedMessage
          id="component.modal.deleteBarnToolsIntegration.text"
          tagName="p"
          values={{
            farmName: integration.farm_name || 'N/A',
            barnName: integration.barn_name || 'N/A',
            gatewayNumber: integration.gateway_number || integration.bt_site_id || 'N/A',
            b: (msg) => <b>{msg}</b>
          }}
        />
      </ConfirmationModal>
    );
  };

  openManageSensorsModal = (integration) => {
    const { openModal } = this.props;
    openModal(
      <ManageSensorsModal
        integration={integration}
        onSensorsUpdate={() => this.fetchIntegrations()}
      />
    );
  };

  handleOpenDropdown = (e, integration) => {
    const { openDropdown } = this.props;
    const options = [
      {
        label: <FormattedMessage id="component.barnTools.manageSensors" />,
        onClick: () => this.openManageSensorsModal(integration),
      },
      {
        label: integration.active ?
          <FormattedMessage id="general.button.disable" /> : <FormattedMessage id="general.button.enable" />,
        onClick: () => {
          if (integration.active) {
            this.handleDeactivate(integration);
          } else {
            this.handleActivate(integration);
          }
        },
      },
      {
        label: 'Delete',
        onClick: () => this.openDeleteConfirmationModal(integration),
      },
    ];
    openDropdown(e, options);
  };

  renderFarmColumn = (integration) => (
    <CustomColumn label={<FormattedMessage id="component.barnTools.table.farm" />}>
      {integration.farm_name || 'N/A'}
    </CustomColumn>
  );

  renderBarnColumn = (integration) => (
    <CustomColumn label={<FormattedMessage id="component.barnTools.table.barn" />}>
      {integration.barn_name || 'N/A'}
    </CustomColumn>
  );

  renderSourceColumn = (integration) => (
    <CustomColumn label={<FormattedMessage id="component.barnTools.table.source" />}>
      {isPublicApiIntegration(integration)
        ? (integration.bt_site_id || 'N/A')
        : (integration.gateway_number || 'N/A')}
    </CustomColumn>
  );

  renderApiColumn = (integration) => (
    <CustomColumn label={<FormattedMessage id="component.barnTools.table.api" />}>
      <FormattedMessage
        id={isPublicApiIntegration(integration)
          ? 'component.barnTools.api.public'
          : 'component.barnTools.api.legacy'}
      />
    </CustomColumn>
  );

  renderSensorsColumn = (integration) => (
    <CustomColumn label={<FormattedMessage id="component.barnTools.sensors" />} className="sensors-column">
      {(() => {
        const list = isPublicApiIntegration(integration) ? integration.sensor_serials : integration.sensors;
        const sensors = Array.isArray(list) ? list : [];
        if (!sensors.length) return 'N/A';
        const fullText = sensors.join(', ');
        return (
          <span className="sensors-text" title={fullText}>{fullText}</span>
        );
      })()}
    </CustomColumn>
  );

  renderStatusColumn = (integration) => (
    <CustomColumn label={<FormattedMessage id="component.barnTools.table.status" />}>
      <StatusBadge status={integration.active ? 'active' : 'disabled'} />
    </CustomColumn>
  );

  renderCreatedAtColumn = (integration) => (
    <CustomColumn label={<FormattedMessage id="component.barnTools.table.createdAt" />}>
      {integration.created_at ? formatDateForUser(integration.created_at, this.props.countryCode) : 'N/A'}
    </CustomColumn>
  );

  renderButtonsColumn = (integration, { rowIndex }) => {
    const { id } = integration;
    return (
      <div className="collapsible-value button-column actions">
        <i
          data-name={`${id}-${rowIndex}`}
          className="fa fa-dots-three-horizontal show-for-large dropdown-icon"
          onClick={(e) => this.handleOpenDropdown(e, integration)}
        />
        <div className="hide-for-large">
          <button
            className="button light"
            onClick={() => (integration.active ? this.handleDeactivate(integration) : this.handleActivate(integration))}
          >
            {integration.active ?
              <FormattedMessage id="general.button.disable" /> : <FormattedMessage id="general.button.enable" />
            }
          </button>
          <button
            className="button light-gray ml-10"
            onClick={() => this.openDeleteConfirmationModal(integration)}
          >
            <FormattedMessage id="general.button.delete" />
          </button>
        </div>
      </div>
    );
  };

  renderExpandable = (integration) => {
    return (
      <div>
        <button
          className="button small light"
          onClick={() => this.openManageSensorsModal(integration)}
        >
          <i className="fa fa-cog mr-5" />
          <FormattedMessage id="component.barnTools.manageSensors" />
        </button>
        <button
          className="button small light"
          onClick={() => (integration.active ? this.handleDeactivate(integration) : this.handleActivate(integration))}
        >
          <i className="fa fa-power-off mr-5" />
          {integration.active ?
            <FormattedMessage id="general.button.disable" /> : <FormattedMessage id="general.button.enable" />
          }
        </button>
        <button
          className="button small light-gray ml-10"
          onClick={() => this.openDeleteConfirmationModal(integration)}
        >
          <i className="fa fa-trash mr-5" />
          <FormattedMessage id="general.button.delete" />
        </button>
      </div>
    );
  };

  getTableColumns = () => [
    {
      label: <FormattedMessage id="component.barnTools.table.farm" />,
      flex: '2 1 150px',
      renderer: this.renderFarmColumn,
      sortKey: 'farm_name'
    },
    {
      label: <FormattedMessage id="component.barnTools.table.barn" />,
      flex: '2 1 150px',
      renderer: this.renderBarnColumn,
      sortKey: 'barn_name'
    },
    {
      label: <FormattedMessage id="component.barnTools.table.source" />,
      flex: '2 1 150px',
      renderer: this.renderSourceColumn,
      sortKey: 'gateway_number'
    },
    {
      label: <FormattedMessage id="component.barnTools.table.api" />,
      flex: '1 1 110px',
      renderer: this.renderApiColumn,
      sortKey: 'api_version'
    },
    {
      label: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FormattedMessage id="component.barnTools.sensors" />
          <InfoTooltip className="ml-5" overlay={<FormattedMessage id="component.barnTools.sensors.hint" />} />
        </div>
      ),
      flex: '3 1 200px',
      renderer: this.renderSensorsColumn,
      sortKey: 'sensors'
    },
    {
      label: <FormattedMessage id="component.barnTools.table.status" />,
      flex: '1 1 100px',
      renderer: this.renderStatusColumn,
      sortKey: 'active'
    },
    {
      label: <FormattedMessage id="component.barnTools.table.createdAt" />,
      flex: '2 1 150px',
      renderer: this.renderCreatedAtColumn,
      sortKey: 'created_at'
    },
    {
      label: '',
      flex: '0 0 45px',
      renderer: this.renderButtonsColumn,
      fixed: true,
      className: isTablet ? 'hide-for-large' : ''
    },
    {
      label: '',
      flex: '0 0 45px',
      renderer: () => <ArrowColumn />,
      fixed: true,
      hide: !isTablet,
      hasPinnedIcon: true
    },
  ];

  render() {
    const { integrations, isLoading, sort, page, per_page, total } = this.state;

    if (isLoading) {
      return <Preloader isActive />;
    }

    if (!integrations.length) {
      return (
        <NothingBox
          itemsName="barn tools integrations"
          display
          isLoading={false}
        >
          <h2 className="lighter">
            <FormattedMessage id="component.barnTools.noIntegrations" />
          </h2>
        </NothingBox>
      );
    }

    const paginationProps = {
      onPageChange: this.handlePageChange,
      onPerPageChange: this.handlePerPageChange,
      totalItems: total,
      currentPage: page,
      perPage: per_page,
    };

    return (
      <div className="BarnToolsList">
        <DataTable
          data={integrations}
          columns={this.getTableColumns()}
          sort={sort}
          onSortChange={this.handleSortChange}
          paginationProps={paginationProps}
          isLoading={isLoading}
          isExpandable={isTablet}
          renderExpandable={this.renderExpandable}
          scrollable
        />
      </div>
    );
  }
}

BarnToolsList.propTypes = {
  companyId: T.number,
  countryCode: T.string,
  openDropdown: T.func.isRequired,
  openModal: T.func.isRequired,
};

const mapStateToProps = (state) => ({
  companyId: state.auth.user?.current_company?.id,
  countryCode: getCurrentCountryCode(state),
});

const mapDispatchToProps = {
  openModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(withDropdownActions(BarnToolsList));
